import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, BarChart3, Activity, Users, AlertTriangle, MessageSquare, Star, Info, ChevronRight,
} from 'lucide-react';
import { getTeacherCourses, getTeacherMetrics } from '../../api/teacher';
import MetricCard from '../../components/MetricCard/MetricCard';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import PageHead from '../../components/PageHead/PageHead';
import CourseSelect from './CourseSelect';
import useTeacherCourse from './useTeacherCourse';
import { failure } from './apiError';
import { formatNumber, formatPercent } from '../../utils/datetime';
import { useI18n } from '../../i18n/I18nContext';
import { pluralSuffix } from './pluralKey';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import './TeacherMetrics.css';

/**
 * Story 9.5-b: Teacher Effectiveness Metrics — darsly-crm Integration
 *
 * Wires GET /teacher/courses/:courseId/metrics
 * Backend returns TeacherMetricsDto:
 *   courseId, avgCompletionRate, avgQuizScore, activeLast7Days,
 *   totalAssigned, atRiskCount, feedbackGiven
 */
function TeacherMetrics() {
  const { t, lang } = useI18n();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, selectCourse] = useTeacherCourse(courses);
  const [metrics, setMetrics] = useState(null);
  // Two questions, two flags: has the course list come back, and has the metrics request for the
  // chosen course settled. One shared "loading" let a slow list answer switch the skeleton off
  // while the numbers were still on their way.
  const [coursesReady, setCoursesReady] = useState(false);
  const [metricsFor, setMetricsFor] = useState(null);
  const [error, setError] = useState(null);
  const [notAssigned, setNotAssigned] = useState(false);
  // The course the latest request was for: an answer for a course the teacher has left is dropped
  const latestCourse = useRef(null);

  const fetchMetrics = useCallback(async () => {
    if (!selectedCourse) return;
    const courseAtCall = selectedCourse;
    latestCourse.current = courseAtCall;
    setMetrics(null);
    setMetricsFor(null);
    try {
      const data = await getTeacherMetrics(courseAtCall);
      if (latestCourse.current !== courseAtCall) return;
      setMetrics(data);
      setError(null);
      setNotAssigned(false);
    } catch (err) {
      if (latestCourse.current !== courseAtCall) return;
      setMetrics(null);
      if (err?.code === 403) {
        setNotAssigned(true);
        setError(null);
      } else {
        setNotAssigned(false);
        setError(failure(err, t('teacher.metrics.failedToLoad')));
      }
    } finally {
      if (latestCourse.current === courseAtCall) setMetricsFor(courseAtCall);
    }
  }, [selectedCourse, t]);

  const fetchCourses = useCallback(async () => {
    try {
      setCoursesReady(false);
      const result = await getTeacherCourses();
      const coursesList = Array.isArray(result) ? result : result.data || [];
      setCourses(coursesList);
      setError(null);
    } catch (err) {
      setError(failure(err, t('teacher.metrics.failedToLoadCourses')));
    } finally {
      setCoursesReady(true);
    }
  }, [t]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  useEffect(() => {
    if (selectedCourse) {
      fetchMetrics();
    }
  }, [selectedCourse, fetchMetrics]);

  const head = (
    <PageHead
      icon={<BarChart3 />}
      title={t('teacher.metrics.title')}
      description={t('teacher.metrics.subtitle')}
      actions={courses.length > 0 ? (
        <CourseSelect
          courses={courses}
          value={selectedCourse}
          onChange={selectCourse}
          ariaLabel={t('teacher.metrics.selectCourse')}
          placeholder={t('teacher.metrics.selectCoursePlaceholder')}
        />
      ) : null}
    />
  );

  // Waiting: for the course list, or (a course chosen) for its numbers
  const loading = !coursesReady || (Boolean(selectedCourse) && metricsFor !== selectedCourse);

  if (loading && !metrics) {
    return (
      <div className="teacher-metrics">
        {head}
        <div className="kpi-row tm-kpis" aria-busy="true" aria-label={t('teacher.metrics.loading')}>
          {Array.from({ length: 6 }).map((_, i) => <MetricCard key={i} loading />)}
        </div>
      </div>
    );
  }

  if (!loading && courses.length === 0) {
    return (
      <div className="teacher-metrics">
        {head}
        {error ? (
          <PageEmptyState variant="error" title={error.title} description={error.detail} onRetry={fetchCourses} />
        ) : (
          <PageEmptyState
            variant="students"
            kind="courses"
            title={t('teacher.metrics.noCoursesTitle')}
            description={t('teacher.metrics.noCoursesDesc')}
          />
        )}
      </div>
    );
  }

  // Derived values for display
  const total = metrics?.totalAssigned || 0;
  const atRiskPercent = total > 0 ? ((metrics.atRiskCount || 0) / total) * 100 : 0;
  const activePercent = total > 0 ? ((metrics.activeLast7Days || 0) / total) * 100 : 0;

  const kpis = metrics ? [
    {
      key: 'completion', icon: <CheckCircle2 />, tone: 'success',
      title: t('teacher.metrics.completionRate'),
      value: formatPercent(metrics.avgCompletionRate || 0),
      change: t('teacher.metrics.completionRateDesc'),
    },
    {
      key: 'average', icon: <BarChart3 />, tone: 'purple',
      title: t('teacher.metrics.avgQuizScore'),
      value: formatPercent(metrics.avgQuizScore || 0),
      change: t('teacher.metrics.avgQuizScoreDesc'),
    },
    {
      key: 'engagement', icon: <Activity />, tone: 'brand',
      title: t('teacher.metrics.activeLast7Days'),
      value: metrics.activeLast7Days || 0,
      change: t('teacher.metrics.activeLast7DaysDesc', { percent: formatNumber(activePercent) }),
    },
    {
      key: 'enrollment', icon: <Users />, tone: 'info',
      title: t('teacher.metrics.totalAssigned'),
      value: total,
      change: t('teacher.metrics.totalAssignedDesc'),
    },
    {
      key: 'at-risk', icon: <AlertTriangle />, tone: 'danger',
      title: t('teacher.metrics.atRiskStudents'),
      value: metrics.atRiskCount || 0,
      change: t('teacher.metrics.atRiskStudentsDesc', { percent: formatNumber(atRiskPercent) }),
    },
    {
      key: 'feedback', icon: <MessageSquare />, tone: 'teal',
      title: t('teacher.metrics.feedbackGiven'),
      value: metrics.feedbackGiven || 0,
      change: t('teacher.metrics.feedbackGivenDesc'),
    },
  ] : [];

  const insights = [];
  if (metrics) {
    if (metrics.atRiskCount > 0) {
      insights.push({
        key: 'warning', tone: 'danger', icon: <AlertTriangle />,
        title: t('teacher.metrics.needAttentionTitle'),
        desc: t(`teacher.metrics.needAttentionDesc${pluralSuffix(lang, metrics.atRiskCount)}`, { count: metrics.atRiskCount }),
        action: (
          <Link to="/teacher/at-risk" className="ds-btn ds-btn-secondary ds-btn-sm">
            {t('teacher.metrics.viewAtRisk')}
            <ChevronRight aria-hidden="true" />
          </Link>
        ),
      });
    }
    if (metrics.avgCompletionRate >= 80) {
      insights.push({
        key: 'completion', tone: 'success', icon: <Star />,
        title: t('teacher.metrics.strongCompletionTitle'),
        desc: t('teacher.metrics.strongCompletionDesc', { percent: formatNumber(metrics.avgCompletionRate || 0) }),
      });
    }
    if (metrics.feedbackGiven === 0 && metrics.totalAssigned > 0) {
      insights.push({
        key: 'feedback', tone: 'info', icon: <Info />,
        title: t('teacher.metrics.noFeedbackTitle'),
        desc: t('teacher.metrics.noFeedbackDesc'),
      });
    }
    if (metrics.atRiskCount === 0 && metrics.totalAssigned > 0) {
      insights.push({
        key: 'on-track', tone: 'success', icon: <CheckCircle2 />,
        title: t('teacher.metrics.noAtRiskTitle'),
        desc: t('teacher.metrics.noAtRiskDesc'),
      });
    }
  }

  return (
    <div className="teacher-metrics">
      {head}

      {notAssigned && (
        <PageEmptyState
          variant="forbidden"
          title={t('teacher.metrics.noCoursesTitle')}
          description={t('teacher.metrics.notAssigned')}
        />
      )}

      {metrics && (
        <>
          {/* 6 key metrics matching TeacherMetricsDto */}
          <div className="kpi-row tm-kpis">
            {kpis.map(({ key, ...tile }) => <MetricCard key={key} {...tile} />)}
          </div>

          {/* Summary */}
          <section className="section-card tm-summary" aria-labelledby="tm-summary-title">
            <div className="section-head">
              <h2 id="tm-summary-title" className="section-head__title">{t('teacher.metrics.summary')}</h2>
            </div>
            {insights.length === 0 ? (
              <InlineEmpty kind="empty" title={t('teacher.metrics.noInsightsYet')} description={t('teacher.metrics.noInsightsDesc')} />
            ) : (
              <div className="ui-list--feed">
                {insights.map((item) => (
                  <div key={item.key} className="ui-list-row tm-insight">
                    <span className="ui-icon-chip ui-icon-chip--md" data-tone={item.tone} aria-hidden="true">{item.icon}</span>
                    <div className="ui-list-row__body tm-insight__body">
                      <h3 className="tm-insight__title">{item.title}</h3>
                      <p className="tm-insight__desc">{item.desc}</p>
                    </div>
                    {item.action}
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {!metrics && !loading && !notAssigned && (
        error ? (
          <PageEmptyState variant="error" title={error.title} description={error.detail} onRetry={fetchMetrics} />
        ) : (
          <PageEmptyState
            variant="default"
            description={t('teacher.metrics.selectCoursePrompt')}
          />
        )
      )}
    </div>
  );
}

export default TeacherMetrics;
